import React, { useState, useEffect } from 'react';
import { useOutletContext, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@shared/components/ui/button';
import { PageHeader, SectionCard, InfoRow } from '@/components/portal/PortalUI';
import { QrCode, Copy, CheckCircle2, Lock, Clock, Loader2, ShieldCheck, ArrowRight, MapPin, AlertTriangle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '@shared/supabaseClient';

// ─────────────────────────────────────────────────────────────────────────────
// PortalDigitalBadge
//
// Shows one QR badge card per site (depot) the user has an active pass for.
// Badge type is site-scoped — not tied to invite type, project, or visitor role.
//
// State machine:
//   loading               — initial fetch in progress
//   no_induction          — no induction submitted
//   induction_awaiting    — submitted, not yet approved
//   induction_pending     — in induction context, pass not yet created
//   project_not_submitted — induction approved, no project in pipeline
//   project_pending       — project submitted, awaiting admin sign-off
//   active                — ≥1 active/suspended pass returned → show cards
// ─────────────────────────────────────────────────────────────────────────────
const PortalDigitalBadge = () => {
  const { session } = useOutletContext();
  const [searchParams] = useSearchParams(); // kept to avoid import error if used elsewhere
  const email         = session?.person?.email    || session?.email;
  const personName    = session?.person?.name     || session?.personName    || 'You';
  const personCompany = session?.person?.company  || session?.personCompany || '';

  const [badgeState, setBadgeState] = useState('loading');
  const [passes,     setPasses]     = useState([]);
  const [copiedId,   setCopiedId]   = useState(null);

  useEffect(() => {
    if (email) loadBadge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // ── Primary loader — one badge per site ─────────────────────────────────────
  const loadBadge = async () => {
    try {
      const { data: rawPasses, error: passError } = await supabase
        .from('passes')
        .select('id, pass_token, status, valid_from, valid_until, person_name, person_company, depot_id, depots:depot_id(name)')
        .ilike('person_email', email)
        .in('status', ['active', 'suspended'])
        .order('valid_from', { ascending: false });

      if (!passError && rawPasses && rawPasses.length > 0) {
        // Deduplicate: one badge per site — keep the most recent active pass per depot
        const byDepot = new Map();
        rawPasses.forEach(p => {
          const key = p.depot_id || 'unknown';
          if (!byDepot.has(key)) {
            byDepot.set(key, {
              pass_id:        p.id,
              pass_token:     p.pass_token,
              pass_status:    p.status,
              valid_from:     p.valid_from,
              valid_until:    p.valid_until,
              person_name:    p.person_name,
              person_company: p.person_company,
              site_name:      p.depots?.name || null,
            });
          }
        });

        const passRows = Array.from(byDepot.values());
        setPasses(passRows);
        setBadgeState('active');
        return;
      }

      // No passes at all — determine which locked-state to show
      await checkBlockingState();
    } catch (err) {
      console.error('Badge load error:', err);
      setBadgeState('no_induction');
    }
  };

  // ── Determine which locked-state message to show ──────────────────────────
  const checkBlockingState = async () => {
    try {
      const { data: subs } = await supabase
        .rpc('portal_get_submission_history', { p_email: email });

      const hasSubmitted = (subs || []).some(s => ['submitted', 'approved'].includes(s.status));
      const hasApproved  = (subs || []).some(s => s.status === 'approved');

      if (!hasSubmitted) { setBadgeState('no_induction');       return; }
      if (!hasApproved)  { setBadgeState('induction_awaiting'); return; }

      const [{ data: leadProjects }, { data: memberRows }] = await Promise.all([
        supabase.from('projects').select('id, status').eq('project_lead_email', email).limit(10),
        supabase.from('project_team_members').select('id, projects(id, status)').eq('person_email', email).limit(10),
      ]);

      const allProjects = [
        ...(leadProjects || []),
        ...((memberRows || []).map(r => r.projects).filter(Boolean)),
      ];

      if (allProjects.length === 0) { setBadgeState('project_not_submitted'); return; }

      const anyApprovedOrActive = allProjects.some(p => p.status === 'approved' || p.status === 'active');
      const anyPending = allProjects.some(p => p.status === 'submitted_awaiting_approval');
      setBadgeState(anyApprovedOrActive || anyPending ? 'project_pending' : 'project_not_submitted');
    } catch {
      setBadgeState('no_induction');
    }
  };

  const handleCopy = async (token, passId) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedId(passId);
      setTimeout(() => setCopiedId(null), 1800);
    } catch (_) {}
  };

  const fmt = (d) =>
    d
      ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      : '—';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Digital Badges"
        subtitle="One badge per site — show the QR code at the site kiosk on arrival."
      />

      {/* ── Loading ─────────────────────────────────────────────────────── */}
      {badgeState === 'loading' && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* ── Active: one card per project pass ───────────────────────────── */}
      {badgeState === 'active' && (
        <div className="space-y-4">
          {passes.map((pass) => (
            <PassCard
              key={pass.pass_id}
              pass={pass}
              personName={personName}
              personCompany={personCompany}
              copied={copiedId === pass.pass_id}
              onCopy={() => handleCopy(pass.pass_token, pass.pass_id)}
              fmt={fmt}
            />
          ))}

          <div className="rounded-lg border border-border/60 bg-muted/30 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
              How check-in works
            </p>
            <p className="text-sm text-muted-foreground">
              Show your QR code at the site kiosk on arrival. Your check-in is recorded automatically.
            </p>
          </div>
        </div>
      )}

      {/* ── Induction awaiting approval ──────────────────────────────────── */}
      {badgeState === 'induction_awaiting' && (
        <LockedPanel
          icon={Clock} iconClass="text-amber-600" iconBg="bg-amber-50 border-amber-300"
          title="Awaiting Induction Approval"
          description="You've submitted your induction. The site team is reviewing it — once approved you can proceed to project review."
          steps={[
            { icon: Clock,       text: 'The site team reviews your induction — usually within one working day.' },
            { icon: ShieldCheck, text: "Once approved, your project lead will submit the project for sign-off." },
            { icon: QrCode,      text: 'Your digital badge appears here automatically after project approval.' },
          ]}
        />
      )}

      {/* ── Project not in pipeline ──────────────────────────────────────── */}
      {badgeState === 'project_not_submitted' && (
        <LockedPanel
          icon={FolderKanban} iconClass="text-primary" iconBg="bg-primary/10 border-primary/30"
          title="Project Not Yet Submitted"
          description="Your induction is complete but your project hasn't been sent for site team review yet."
          steps={[
            { icon: FolderKanban, text: 'Go to Projects and submit your project for review.' },
            { icon: Clock,        text: 'The site team will review your project and all team inductions.' },
            { icon: QrCode,       text: 'Once approved, your digital badge is issued automatically.' },
          ]}
          action={{ label: 'Go to Projects', to: '/portal/projects' }}
        />
      )}

      {/* ── Project awaiting admin sign-off ──────────────────────────────── */}
      {badgeState === 'project_pending' && (
        <LockedPanel
          icon={Clock} iconClass="text-amber-600" iconBg="bg-amber-50 border-amber-300"
          title="Awaiting Site Team Sign-Off"
          description="Your project has been submitted. Your digital badge will appear here automatically once approved."
          steps={[
            { icon: Clock,       text: 'The site team reviews your project and all team inductions.' },
            { icon: ShieldCheck, text: "Once approved, you'll receive a confirmation email." },
            { icon: QrCode,      text: 'Your digital badge will appear here automatically — no action needed.' },
          ]}
        />
      )}

      {/* ── Induction awaiting approval ──────────────────────────────────── */}
      {badgeState === 'induction_pending' && (
        <LockedPanel
          icon={Clock} iconClass="text-amber-600" iconBg="bg-amber-50 border-amber-300"
          title="Awaiting Approval"
          description="Your induction has been submitted. Your digital badge will appear here once it has been approved by the site team."
          steps={[
            { icon: Clock,       text: 'The site team will review your induction submission.' },
            { icon: ShieldCheck, text: "Once approved, you'll receive a confirmation email." },
            { icon: QrCode,      text: 'Your digital badge will appear here automatically — no action needed.' },
          ]}
        />
      )}

      {/* ── No induction ─────────────────────────────────────────────────── */}
      {badgeState === 'no_induction' && (
        <div className="max-w-2xl mx-auto">
          <SectionCard>
            <div className="flex flex-col items-center text-center py-4 gap-4">
              <div className="w-20 h-20 rounded-full bg-muted border-2 border-border flex items-center justify-center">
                <Lock className="h-10 w-10 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground">Badge Locked</h3>
                <p className="text-sm font-medium text-muted-foreground mt-1.5 max-w-sm mx-auto">
                  Complete your site induction to unlock your digital badge.
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-border bg-muted/30 p-4 space-y-2">
              {[
                { n: 1, text: 'Complete your induction brief' },
                { n: 2, text: 'Site team reviews and approves' },
                { n: 3, text: 'Await project sign-off' },
                { n: 4, text: 'Digital badge issued automatically' },
              ].map(({ n, text }) => (
                <div key={n} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-primary text-xs font-bold">
                    {n}
                  </div>
                  <p className="text-sm font-semibold text-foreground">{text}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 flex justify-center">
              <Button asChild>
                <Link to="/portal/inductions">
                  Start Your Induction <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </SectionCard>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PassCard — one card per site pass
// ─────────────────────────────────────────────────────────────────────────────
const PassCard = ({ pass, personName, personCompany, copied, onCopy, fmt }) => {
  const isSuspended = pass.pass_status === 'suspended';

  return (
    <div
      className={`rounded-xl border shadow-sm overflow-hidden ${
        isSuspended ? 'border-amber-300 bg-amber-50/40' : 'border-border bg-card'
      }`}
    >
      {/* Header */}
      <div
        className={`px-5 py-3 flex items-center justify-between border-b ${
          isSuspended ? 'bg-amber-100/60 border-amber-200' : 'bg-muted/40 border-border'
        }`}
      >
        <div className="flex items-center gap-2">
          <MapPin className={`h-4 w-4 ${isSuspended ? 'text-amber-600' : 'text-primary'}`} />
          <span className="text-sm font-bold text-foreground">
            {pass.site_name || 'Site Badge'}
          </span>
        </div>
        {isSuspended ? (
          <span className="flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-2.5 py-0.5">
            <AlertTriangle className="h-3 w-3" /> Suspended
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-xs font-bold text-green-700 bg-green-100 border border-green-300 rounded-full px-2.5 py-0.5">
            <CheckCircle2 className="h-3 w-3" /> Active
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-5">
        {isSuspended ? (
          <div className="flex items-start gap-4">
            <div className="rounded-xl border-2 border-amber-300/60 bg-amber-50 p-4 flex items-center justify-center w-[148px] h-[148px] flex-shrink-0">
              <Lock className="h-12 w-12 text-amber-400" />
            </div>
            <div className="flex-1 space-y-2 pt-1">
              <p className="text-sm font-semibold text-amber-800">This site badge has been temporarily suspended.</p>
              <p className="text-sm text-amber-700">
                Your badge for this site has been placed on hold by the site team.
                Contact the site team for more information.
              </p>
              <div className="pt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />
                <span>{pass.site_name || 'Site not specified'}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-5 md:flex-row md:items-start">
            {/* QR code */}
            <div className="rounded-xl border-2 border-border bg-white p-3 shadow-sm flex-shrink-0 self-center md:self-start">
              <QRCodeSVG value={`APEX:${pass.pass_token}`} size={172} />
            </div>

            {/* Details */}
            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-1 gap-y-0 sm:grid-cols-2 sm:gap-x-6">
                <InfoRow label="Valid from"  value={fmt(pass.valid_from)} />
                <InfoRow label="Valid until" value={fmt(pass.valid_until)} />
                <InfoRow label="Site"        value={pass.site_name || '—'} />
                <InfoRow label="Person"      value={personName} />
                {personCompany && <InfoRow label="Company" value={personCompany} />}
              </div>

              <div>
                <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">
                  Pass token
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="rounded-md border border-border bg-muted/40 px-3 py-1.5 font-mono text-sm text-foreground">
                    {pass.pass_token}
                  </p>
                  <Button variant="outline" size="sm" onClick={onCopy}>
                    {copied ? (
                      <><CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />Copied</>
                    ) : (
                      <><Copy className="mr-1.5 h-3.5 w-3.5" />Copy</>
                    )}
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5">
                <p className="text-xs text-muted-foreground font-medium">
                  Show this QR at the <strong>kiosk on site</strong>. Keep your token private.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// LockedPanel — reusable guidance block for locked states
// ─────────────────────────────────────────────────────────────────────────────
const LockedPanel = ({ icon: Icon, iconClass, iconBg, title, description, steps, action }) => (
  <div className="max-w-2xl mx-auto">
    <SectionCard>
      <div className="flex flex-col items-center text-center py-4 gap-4">
        <div className={`w-20 h-20 rounded-full border-2 flex items-center justify-center ${iconBg}`}>
          <Icon className={`h-10 w-10 ${iconClass}`} />
        </div>
        <div>
          <h3 className="text-xl font-bold text-foreground">{title}</h3>
          <p className="text-sm font-medium text-muted-foreground mt-1.5 max-w-sm mx-auto">{description}</p>
        </div>
      </div>

      {steps?.length > 0 && (
        <div className="mt-5 space-y-2.5">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">What happens next</p>
          {steps.map(({ icon: StepIcon, text }, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <StepIcon className="h-3.5 w-3.5 text-primary" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">{text}</p>
            </div>
          ))}
        </div>
      )}

      {action && (
        <div className="mt-5 flex justify-center">
          <Button asChild>
            <Link to={action.to}>
              {action.label} <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      )}
    </SectionCard>
  </div>
);

export default PortalDigitalBadge;
